import {
  Badge,
  Body1Strong,
  Button,
  Caption1,
  Dropdown,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  makeStyles,
  Option,
  Spinner,
  Switch,
  tokens,
} from "@fluentui/react-components";
import { AddRegular, FormNewRegular } from "@fluentui/react-icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { type AttributeDefinition, type AttributeOption, catalogue } from "../api/catalogue";
import { ApiError } from "../api/client";
import { type Category, commerce } from "../api/commerce";
import { baeTypography } from "../theme/baeTheme";
import { EmptyState, Page, PageHeader } from "../ui/PageHeader";
import { Card } from "../ui/Surface";
import { PHONE } from "../ui/tokens";

const useStyles = makeStyles({
  heading: { fontFamily: baeTypography.fontFamilyDisplay, letterSpacing: "-0.02em" },
  panel: {
    display: "grid",
    gap: tokens.spacingVerticalM,
    padding: tokens.spacingHorizontalL,
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    minWidth: 0,
    [PHONE]: { padding: tokens.spacingHorizontalM },
  },
  row: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: tokens.spacingHorizontalM,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
  },
  rowBody: { display: "grid", gap: "2px", flexGrow: 1, minWidth: 0 },
  dim: { opacity: 0.55 },
  muted: { color: tokens.colorNeutralForeground3 },
  key: { fontFamily: baeTypography.fontFamilyMonospace, fontSize: "12px" },
  pair: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(12rem, 1fr))",
    gap: tokens.spacingHorizontalM,
  },
  optionRow: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr auto",
    gap: tokens.spacingHorizontalS,
    alignItems: "end",
    // Two text boxes and a button do not fit across a phone. Stacked, with the button on its own
    // line, is legible; squeezed to 4rem each is not.
    [PHONE]: { gridTemplateColumns: "1fr" },
  },
  axisNote: {
    display: "grid",
    gap: tokens.spacingVerticalXXS,
    padding: tokens.spacingHorizontalM,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground3,
  },
  actions: { display: "flex", gap: tokens.spacingHorizontalS, flexWrap: "wrap" },
  list: { display: "grid", gap: tokens.spacingVerticalS },
});

const DATA_TYPES: { value: AttributeDefinition["dataType"]; label: string }[] = [
  { value: "Select", label: "Pick one from a list" },
  { value: "MultiSelect", label: "Tick any number" },
  { value: "Text", label: "Short text" },
  { value: "LongText", label: "Long text" },
  { value: "Number", label: "Number" },
  { value: "Boolean", label: "Yes or no" },
  { value: "Date", label: "Date" },
];

const typeLabel = (type: AttributeDefinition["dataType"]) =>
  DATA_TYPES.find((t) => t.value === type)?.label ?? type;

const hasOptions = (type: AttributeDefinition["dataType"]) =>
  type === "Select" || type === "MultiSelect";

/**
 * Derives a stable key from the field name, so nobody has to invent one.
 *
 * The key is what product values are stored against, which is why it is set once when the field is
 * created and left alone afterwards: renaming "Cloth material" to "Fabric" must not orphan the
 * values already recorded against it.
 */
const keyFrom = (name: string) =>
  name
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 40);

type Draft = {
  id: string | null;
  key: string;
  name: string;
  hint: string;
  dataType: AttributeDefinition["dataType"];
  categoryId: string;
  isRequired: boolean;
  sortOrder: number;
  isVariantAxis: boolean;
  options: AttributeOption[];
};

const emptyDraft = (sortOrder: number): Draft => ({
  id: null,
  key: "",
  name: "",
  hint: "",
  dataType: "Select",
  categoryId: "",
  isRequired: false,
  sortOrder,
  isVariantAxis: false,
  options: [{ value: "", label: "" }],
});

const draftFrom = (definition: AttributeDefinition): Draft => ({
  id: definition.id,
  key: definition.key,
  name: definition.name,
  hint: definition.hint ?? "",
  dataType: definition.dataType,
  categoryId: definition.categoryId ?? "",
  isRequired: definition.isRequired,
  sortOrder: definition.sortOrder,
  isVariantAxis: definition.isVariantAxis,
  options: definition.options.length > 0 ? [...definition.options] : [{ value: "", label: "" }],
});

/**
 * Product fields, as the tenant owner sees them.
 *
 * Everything on the Add a piece form below the name comes from here. The owner adds a field, gives
 * it choices, and the phone form grows a dropdown — no release required (ADR-0009).
 */
export function AttributeSettingsPage() {
  const styles = useStyles();
  const queryClient = useQueryClient();

  const [draft, setDraft] = useState<Draft | null>(null);

  const definitions = useQuery({
    queryKey: ["attributes", "all"],
    queryFn: () => catalogue.attributes(),
  });
  const categories = useQuery({ queryKey: ["categories"], queryFn: commerce.categories });

  const sorted = useMemo(
    () => [...(definitions.data ?? [])].sort((a, b) => a.sortOrder - b.sortOrder),
    [definitions.data],
  );

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ["attributes"] });
  };

  const save = useMutation({
    mutationFn: (value: Draft) =>
      catalogue.saveAttribute({
        key: value.key || keyFrom(value.name),
        name: value.name.trim(),
        dataType: value.dataType,
        hint: value.hint.trim() || null,
        categoryId: value.categoryId || null,
        isRequired: value.isRequired,
        sortOrder: value.sortOrder,
        // Only a pick-one field can be an axis. Kept true here as well as in the API so the
        // toggle cannot be left on by switching the type afterwards.
        isVariantAxis: value.isVariantAxis && value.dataType === "Select",
        options: hasOptions(value.dataType)
          ? value.options
              .filter((o) => o.label.trim() !== "")
              .map((o) => ({
                value: (o.value.trim() || keyFrom(o.label)).toUpperCase(),
                label: o.label.trim(),
              }))
          : null,
      }),
    onSuccess: () => {
      setDraft(null);
      invalidate();
    },
  });

  const toggle = useMutation({
    mutationFn: (definition: AttributeDefinition) =>
      definition.isActive
        ? catalogue.deactivateAttribute(definition.id)
        : catalogue.reactivateAttribute(definition.id),
    onSuccess: invalidate,
  });

  // The full path, because a field attached to "Long" needs to say which Long.
  const categoryName = (id: string | null) =>
    id
      ? ((categories.data ?? []).find((c) => c.id === id)?.path.join(" / ") ?? "one category")
      : "Everything";

  const nextSortOrder = (sorted.at(-1)?.sortOrder ?? 0) + 10;

  return (
    <Page width="medium">
      <PageHeader
        title="Product fields"
        subtitle="The questions the team is asked when adding a piece. Lists and tick-boxes are quicker on a phone than typing, and they keep the data tidy enough to search on later."
        actions={
          !draft ? (
            <Button
              appearance="primary"
              icon={<AddRegular />}
              onClick={() => setDraft(emptyDraft(nextSortOrder))}
            >
              Add a field
            </Button>
          ) : undefined
        }
      />

      {(save.error instanceof ApiError || toggle.error instanceof ApiError) && (
        <MessageBar intent="error">
          <MessageBarBody>
            <MessageBarTitle>Could not save</MessageBarTitle>
            {(save.error ?? toggle.error)?.message}
          </MessageBarBody>
        </MessageBar>
      )}

      {draft ? (
        <DraftEditor
          draft={draft}
          categories={categories.data ?? []}
          busy={save.isPending}
          onChange={setDraft}
          onCancel={() => setDraft(null)}
          onSave={() => save.mutate(draft)}
        />
      ) : null}

      {definitions.isLoading ? (
        <Spinner label="Loading fields" />
      ) : sorted.length === 0 ? (
        <Card>
          <EmptyState
            icon={<FormNewRegular />}
            title="No fields yet"
            action={
              <Button
                appearance="primary"
                icon={<AddRegular />}
                onClick={() => setDraft(emptyDraft(nextSortOrder))}
              >
                Add the first one
              </Button>
            }
          >
            Cloth material, art work, colour, size — whatever the team needs to record about a
            piece.
          </EmptyState>
        </Card>
      ) : (
        <div className={styles.list}>
          {sorted.map((definition) => (
            <div
              key={definition.id}
              className={`${styles.row} ${definition.isActive ? "" : styles.dim}`}
            >
              <div className={styles.rowBody}>
                <Body1Strong>{definition.name}</Body1Strong>
                <Caption1 className={styles.muted}>
                  <span className={styles.key}>{definition.key}</span> ·{" "}
                  {typeLabel(definition.dataType)}
                  {hasOptions(definition.dataType) ? ` · ${definition.options.length} choices` : ""}{" "}
                  · {categoryName(definition.categoryId)}
                </Caption1>
              </div>

              {definition.isVariantAxis && (
                <Badge appearance="tint" color="brand" size="small">
                  Makes SKUs
                </Badge>
              )}
              {definition.isRequired && (
                <Badge appearance="tint" color="informative" size="small">
                  Required
                </Badge>
              )}
              {!definition.isActive && (
                <Badge appearance="tint" size="small">
                  Hidden
                </Badge>
              )}

              <div className={styles.actions}>
                <Button appearance="subtle" onClick={() => setDraft(draftFrom(definition))}>
                  Edit
                </Button>
                <Button appearance="subtle" onClick={() => toggle.mutate(definition)}>
                  {definition.isActive ? "Hide" : "Show"}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Caption1 className={styles.muted}>
        Hiding a field takes it off the form but keeps what has already been recorded against it.
        The same goes for removing a choice: pieces already marked with it keep their answer.
      </Caption1>
    </Page>
  );
}

function DraftEditor({
  draft,
  categories,
  busy,
  onChange,
  onCancel,
  onSave,
}: {
  draft: Draft;
  categories: Category[];
  busy: boolean;
  onChange: (next: Draft) => void;
  onCancel: () => void;
  onSave: () => void;
}) {
  const styles = useStyles();
  const isNew = draft.id === null;

  const setOption = (index: number, next: AttributeOption) =>
    onChange({
      ...draft,
      options: draft.options.map((option, i) => (i === index ? next : option)),
    });

  const filledOptions = draft.options.filter((o) => o.label.trim() !== "");
  const canSave =
    draft.name.trim() !== "" && (!hasOptions(draft.dataType) || filledOptions.length > 0) && !busy;

  return (
    <div className={styles.panel}>
      <Body1Strong>{isNew ? "New field" : `Editing ${draft.name}`}</Body1Strong>

      <div className={styles.pair}>
        <Field label="Question" required hint="What the team sees on the form.">
          <Input
            value={draft.name}
            placeholder="Cloth material"
            onChange={(_, d) =>
              onChange({
                ...draft,
                name: d.value,
                // Derived only while it is new: the key is what values are stored against.
                key: isNew ? keyFrom(d.value) : draft.key,
              })
            }
          />
        </Field>

        <Field
          label="Answered how?"
          hint={isNew ? undefined : "Cannot change once pieces have been recorded."}
        >
          <Dropdown
            disabled={!isNew}
            value={typeLabel(draft.dataType)}
            selectedOptions={[draft.dataType]}
            onOptionSelect={(_, data) =>
              onChange({ ...draft, dataType: data.optionValue as Draft["dataType"] })
            }
          >
            {DATA_TYPES.map((type) => (
              <Option key={type.value} value={type.value} text={type.label}>
                {type.label}
              </Option>
            ))}
          </Dropdown>
        </Field>
      </div>

      <div className={styles.pair}>
        <Field label="Applies to" hint="Leave as Everything to ask it for every product.">
          <Dropdown
            value={
              draft.categoryId
                ? (categories.find((c) => c.id === draft.categoryId)?.path.join(" / ") ?? "")
                : "Everything"
            }
            selectedOptions={[draft.categoryId || "all"]}
            onOptionSelect={(_, data) =>
              onChange({
                ...draft,
                categoryId: data.optionValue === "all" ? "" : (data.optionValue ?? ""),
              })
            }
          >
            <Option value="all" text="Everything">
              Everything
            </Option>
            {categories.map((category) => (
              <Option key={category.id} value={category.id} text={category.path.join(" / ")}>
                {category.path.join(" / ")}
              </Option>
            ))}
          </Dropdown>
        </Field>

        <Field label="Hint" hint="Optional. Shown under the question.">
          <Input value={draft.hint} onChange={(_, d) => onChange({ ...draft, hint: d.value })} />
        </Field>
      </div>

      <Switch
        label="Must be answered"
        checked={draft.isRequired}
        onChange={(_, d) => onChange({ ...draft, isRequired: d.checked })}
      />

      {draft.dataType === "Select" && (
        <div className={styles.axisNote}>
          <Switch
            label="Use this to make SKUs"
            checked={draft.isVariantAxis}
            onChange={(_, d) => onChange({ ...draft, isVariantAxis: d.checked })}
          />
          <Caption1 className={styles.muted}>
            {draft.isVariantAxis
              ? `When a piece comes in more than one ${(draft.name || "choice").toLowerCase()}, the team ticks the ones they have and each gets its own SKU, its own count and its own barcode.`
              : "Turn this on for something like colour or size — anything where the same design in two of them is two things to count separately."}
          </Caption1>
        </div>
      )}

      {hasOptions(draft.dataType) && (
        <>
          <Body1Strong>Choices</Body1Strong>
          {draft.options.map((option, index) => (
            // The index is the identity here: these rows are a working list being typed into, and
            // reordering is not offered.
            // biome-ignore lint/suspicious/noArrayIndexKey: rows are positional while editing
            <div key={index} className={styles.optionRow}>
              <Field label={index === 0 ? "Shown as" : undefined}>
                <Input
                  value={option.label}
                  placeholder="Mul cotton"
                  onChange={(_, d) => setOption(index, { ...option, label: d.value })}
                />
              </Field>
              <Field label={index === 0 ? "Stored as" : undefined}>
                <Input
                  value={option.value}
                  placeholder={keyFrom(option.label) || "MUL_COTTON"}
                  onChange={(_, d) => setOption(index, { ...option, value: d.value })}
                />
              </Field>
              <Button
                appearance="subtle"
                disabled={draft.options.length === 1}
                onClick={() =>
                  onChange({ ...draft, options: draft.options.filter((_, i) => i !== index) })
                }
              >
                Remove
              </Button>
            </div>
          ))}

          <div>
            <Button
              appearance="secondary"
              onClick={() =>
                onChange({ ...draft, options: [...draft.options, { value: "", label: "" }] })
              }
            >
              Add a choice
            </Button>
          </div>
        </>
      )}

      <div className={styles.actions}>
        <Button appearance="primary" disabled={!canSave} onClick={onSave}>
          {busy ? <Spinner size="tiny" /> : "Save field"}
        </Button>
        <Button appearance="subtle" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}
